import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PublicIcon from '@mui/icons-material/Public';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';

import { useAlert } from '../context/AlertContext';
import { useGetAppArray } from '../hooks/reactQuerys/AppQueries';
import { useSetRequestClientData } from '../hooks/reactQuerys/ClientDataRequestQueries';
import { useGetActiveReadingSessions } from '../hooks/reactQuerys/TabletReadingQueries';
import {
  useCancelTabletSubmission,
  useGetTabletSubmissionsForLegalTransaction,
  useRejectTabletSubmission,
} from '../hooks/reactQuerys/TabletSubmissionQueries';
import { IDocument } from '../Interfaces/IDocument';
import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  ITabletSubmission,
  ITabletSubmissionStatus,
} from '../Interfaces/ITabletSubmission';
import { ActiveReadingSessionPanel } from './ActiveReadingSessionPanel';
import { SendToTabletDialog } from './SendToTabletDialog';
import { StartReadingDialog } from './StartReadingDialog';
import { TabletSubmissionDetailDialog } from './TabletSubmissionDetailDialog';

const STATUS_LABEL: Record<ITabletSubmissionStatus, string> = {
  pending_input: 'Wartet auf Eingabe',
  submitted: 'Eingang vom Tablet',
  accepted: 'Übernommen',
  rejected: 'Abgelehnt',
  cancelled: 'Abgebrochen',
};

const STATUS_COLOR: Record<
  ITabletSubmissionStatus,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending_input: 'info',
  submitted: 'primary',
  accepted: 'success',
  rejected: 'error',
  cancelled: 'default',
};

interface Props {
  idLegalTransaction: number;
  participants: ILegalClient[];
  drafts?: IDocument[];
  certificates?: IDocument[];
  otherDocuments?: IDocument[];
}

function formatDate(value?: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE');
}

function extractDocumentExpiry(
  submission: ITabletSubmission | undefined
): string | null {
  if (!submission?.PrefillPayload) return null;
  try {
    const raw =
      typeof submission.PrefillPayload === 'string'
        ? JSON.parse(submission.PrefillPayload)
        : submission.PrefillPayload;
    return raw?.Person?.DocumentExpiry ?? null;
  } catch {
    return null;
  }
}

function renderExpiryChip(submission: ITabletSubmission | undefined) {
  const expiry = extractDocumentExpiry(submission);
  if (!expiry) return null;
  const exp = new Date(expiry);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  const days = Math.floor(
    (exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days < 0) {
    return (
      <Tooltip
        title={`Personalausweis abgelaufen am ${exp.toLocaleDateString('de-DE')}`}
      >
        <Chip label='Ausweis abgelaufen' size='small' color='error' />
      </Tooltip>
    );
  }
  if (days <= 90) {
    return (
      <Tooltip
        title={`Personalausweis läuft am ${exp.toLocaleDateString('de-DE')} ab`}
      >
        <Chip
          label={`Ausweis läuft in ${days} T. ab`}
          size='small'
          color='warning'
        />
      </Tooltip>
    );
  }
  return null;
}

/**
 * Wählt für jeden Beteiligten die "aktivste" Submission aus:
 * pending_input > submitted > accepted > rejected > cancelled, jeweils neueste.
 */
const STATUS_RANK: Record<ITabletSubmissionStatus, number> = {
  pending_input: 5,
  submitted: 4,
  accepted: 3,
  rejected: 2,
  cancelled: 1,
};

function pickMostRelevant(
  arr: ITabletSubmission[]
): ITabletSubmission | undefined {
  if (!arr.length) return undefined;
  return [...arr].sort((a, b) => {
    const r = STATUS_RANK[b.Status] - STATUS_RANK[a.Status];
    if (r !== 0) return r;
    return (b.Created_at ?? '').localeCompare(a.Created_at ?? '');
  })[0];
}

export const TabletActionPanel: React.FC<Props> = ({
  idLegalTransaction,
  participants,
  drafts,
  certificates,
  otherDocuments,
}) => {
  const { appArray } = useGetAppArray();
  const tabletServiceActive =
    appArray?.find(a => a.system_key === 'dino_tablet_service')?.IsEffective ===
    true;

  const { submissions } = useGetTabletSubmissionsForLegalTransaction(
    tabletServiceActive ? idLegalTransaction : null
  );
  const { sessions: readingSessions } = useGetActiveReadingSessions(
    tabletServiceActive ? idLegalTransaction : null
  );

  const { rejectSubmission, isPendingRejectSubmission } =
    useRejectTabletSubmission();
  const { cancelSubmission, isPendingCancelSubmission } =
    useCancelTabletSubmission();
  const { setRequestClientData, isSettingRequestClientData } =
    useSetRequestClientData();
  const { showAlert } = useAlert();

  // Auswahl der beiden Checkbox-Spalten (Portal-Pruefung / Tablet-Service).
  const [portalSel, setPortalSel] = useState<Set<number>>(new Set());
  const [tabletSel, setTabletSel] = useState<Set<number>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPrefillClient, setDialogPrefillClient] = useState<
    number | null | undefined
  >(undefined);
  const [detailFor, setDetailFor] = useState<ITabletSubmission | null>(null);
  const [readingDialogOpen, setReadingDialogOpen] = useState(false);

  const readingDocs = useMemo(
    () => [
      ...(drafts ?? []).map(d => ({ ...d, Kind: 'Entwurf' })),
      ...(certificates ?? []).map(d => ({ ...d, Kind: 'Urkunde' })),
      ...(otherDocuments ?? []).map(d => ({ ...d, Kind: 'Sonstiges' })),
    ],
    [drafts, certificates, otherDocuments]
  );

  // Submissions nach LegalClient-Zuordnung sortieren
  const { byParticipant, unbound } = useMemo(() => {
    const map = new Map<number, ITabletSubmission[]>();
    const unboundList: ITabletSubmission[] = [];
    for (const s of submissions ?? []) {
      if (s.LegalClient_idLegalClient) {
        const arr = map.get(s.LegalClient_idLegalClient) ?? [];
        arr.push(s);
        map.set(s.LegalClient_idLegalClient, arr);
      } else {
        unboundList.push(s);
      }
    }
    return { byParticipant: map, unbound: unboundList };
  }, [submissions]);

  const isMutating = isPendingRejectSubmission || isPendingCancelSubmission;

  if (!tabletServiceActive) return null;

  function openSendDialog(prefillIdLegalClient?: number | null) {
    setDialogPrefillClient(prefillIdLegalClient ?? null);
    setDialogOpen(true);
  }

  const participantIds = participants.map(p => p.idLegalClient);
  const allPortalChecked =
    participantIds.length > 0 && participantIds.every(id => portalSel.has(id));
  const somePortalChecked = participantIds.some(id => portalSel.has(id));
  const allTabletChecked =
    participantIds.length > 0 && participantIds.every(id => tabletSel.has(id));
  const someTabletChecked = participantIds.some(id => tabletSel.has(id));

  function toggleIn(
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    id: number
  ) {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    checked: boolean
  ) {
    setter(checked ? new Set(participantIds) : new Set());
  }

  async function handleRequestPortal() {
    const ids = Array.from(portalSel);
    if (!ids.length) {
      showAlert({
        text: 'Bitte mindestens einen Beteiligten für die Portal-Prüfung auswählen.',
        severity: 'warning',
      });
      return;
    }
    try {
      await setRequestClientData({
        idLegalTransaction,
        value: true,
        clientIds: ids,
      });
      showAlert({
        text: `Portal-Aufforderung an ${ids.length} Beteiligte(n) versendet. Die Selbsteinreichung erscheint im Mandantenportal.`,
        severity: 'success',
      });
      setPortalSel(new Set());
    } catch {
      showAlert({
        text: 'Die Portal-Aufforderung ist fehlgeschlagen.',
        severity: 'error',
      });
    }
  }

  function handleSendTablet() {
    const ids = Array.from(tabletSel);
    if (!ids.length) {
      showAlert({
        text: 'Bitte mindestens einen Beteiligten für den Tablet-Service auswählen.',
        severity: 'warning',
      });
      return;
    }
    // Tablet-Versand braucht immer eine Geraeteauswahl -> Dialog. Bei genau
    // einem Beteiligten direkt vorbelegt, sonst freier Modus (mehrere Tablets).
    openSendDialog(ids.length === 1 ? ids[0] : null);
  }

  async function handleReject(s: ITabletSubmission) {
    if (!window.confirm('Diese Eingabe wirklich verwerfen?')) return;
    try {
      await rejectSubmission(s.idTabletSubmission);
    } catch {
      showAlert({ text: 'Ablehnung fehlgeschlagen.', severity: 'error' });
    }
  }

  async function handleCancel(s: ITabletSubmission) {
    if (!window.confirm('Auftrag wirklich abbrechen?')) return;
    try {
      await cancelSubmission(s.idTabletSubmission);
    } catch {
      showAlert({ text: 'Abbruch fehlgeschlagen.', severity: 'error' });
    }
  }

  return (
    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 2,
        mt: 2,
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        mb={1}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <TabletAndroidIcon color='primary' />
          <Typography variant='h6'>Prüfung anfordern</Typography>
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<MenuBookIcon />}
            onClick={() => setReadingDialogOpen(true)}
            disabled={(readingSessions ?? []).length > 0}
          >
            Urkunde mitlesen
          </Button>
          <Button
            variant='outlined'
            startIcon={<PublicIcon />}
            onClick={handleRequestPortal}
            disabled={!somePortalChecked || isSettingRequestClientData}
          >
            Portal-Prüfung anfordern
            {somePortalChecked ? ` (${portalSel.size})` : ''}
          </Button>
          <Button
            variant='contained'
            startIcon={<SendIcon />}
            onClick={handleSendTablet}
            disabled={!someTabletChecked}
          >
            An Tablet senden
            {someTabletChecked ? ` (${tabletSel.size})` : ''}
          </Button>
        </Stack>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Wählen Sie je Beteiligtem, wie die Ausweis- und Stammdaten geprüft
        werden sollen: per Selbsteinreichung im Mandantenportal (vor dem Termin)
        oder per Tablet bei der Beurkundung. Eingegangene Erfassungen sind mit
        einem Haken markiert und können angesehen werden.
      </Typography>

      {(readingSessions ?? []).map(s => (
        <ActiveReadingSessionPanel key={s.idTabletReadingSession} session={s} />
      ))}

      {participants.length === 0 && unbound.length === 0 && (
        <Typography variant='body2' color='text.secondary'>
          Es sind keine Beteiligten am Vorgang. Bitte zuerst einen Beteiligten
          hinzufügen, dann an ein Tablet senden.
        </Typography>
      )}

      {participants.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Beteiligter</TableCell>
              <TableCell align='center' padding='checkbox'>
                <Stack alignItems='center'>
                  <Typography variant='caption'>Portal-Prüfung</Typography>
                  <Checkbox
                    size='small'
                    checked={allPortalChecked}
                    indeterminate={somePortalChecked && !allPortalChecked}
                    onChange={e => toggleAll(setPortalSel, e.target.checked)}
                  />
                </Stack>
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                <Stack alignItems='center'>
                  <Typography variant='caption'>Tablet</Typography>
                  <Checkbox
                    size='small'
                    checked={allTabletChecked}
                    indeterminate={someTabletChecked && !allTabletChecked}
                    onChange={e => toggleAll(setTabletSel, e.target.checked)}
                  />
                </Stack>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tablet-Gerät</TableCell>
              <TableCell>Eingegeben</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participants.map(p => {
              const subs = byParticipant.get(p.idLegalClient) ?? [];
              const active = pickMostRelevant(subs);
              const name =
                [p.FirstName, p.LastName].filter(Boolean).join(' ') ||
                `Beteiligter #${p.idLegalClient}`;
              return (
                <TableRow key={p.idLegalClient} hover>
                  <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                      {name}
                    </Typography>
                  </TableCell>
                  <TableCell align='center' padding='checkbox'>
                    <Checkbox
                      size='small'
                      checked={portalSel.has(p.idLegalClient)}
                      onChange={() => toggleIn(setPortalSel, p.idLegalClient)}
                    />
                  </TableCell>
                  <TableCell align='center' padding='checkbox'>
                    <Checkbox
                      size='small'
                      checked={tabletSel.has(p.idLegalClient)}
                      onChange={() => toggleIn(setTabletSel, p.idLegalClient)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      flexWrap='wrap'
                      alignItems='center'
                    >
                      {active &&
                      (active.Status === 'submitted' ||
                        active.Status === 'accepted') ? (
                        <Tooltip
                          title={`Eingegangen${
                            active.Source === 'portal'
                              ? ' (Portal)'
                              : active.Source === 'tablet'
                                ? ' (Tablet)'
                                : ''
                          }`}
                        >
                          <CheckCircleIcon color='success' fontSize='small' />
                        </Tooltip>
                      ) : null}
                      {active ? (
                        <Chip
                          label={STATUS_LABEL[active.Status]}
                          color={STATUS_COLOR[active.Status]}
                          size='small'
                        />
                      ) : (
                        <Chip
                          label='Noch nichts angefordert'
                          size='small'
                          variant='outlined'
                        />
                      )}
                      {active?.Source ? (
                        <Chip
                          label={
                            active.Source === 'portal' ? 'Portal' : 'Tablet'
                          }
                          size='small'
                          variant='outlined'
                          icon={
                            active.Source === 'portal' ? (
                              <PublicIcon />
                            ) : (
                              <TabletAndroidIcon />
                            )
                          }
                        />
                      ) : null}
                      {renderExpiryChip(active)}
                    </Stack>
                  </TableCell>
                  <TableCell>{active?.TabletName ?? '–'}</TableCell>
                  <TableCell>
                    {active?.SubmittedAt ? formatDate(active.SubmittedAt) : '–'}
                  </TableCell>
                  <TableCell align='right'>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      justifyContent='flex-end'
                    >
                      {!active && (
                        <Tooltip title='An Tablet senden'>
                          <span>
                            <IconButton
                              color='primary'
                              onClick={() => openSendDialog(p.idLegalClient)}
                            >
                              <SendIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {active && active.Status === 'pending_input' && (
                        <Tooltip title='Auftrag abbrechen'>
                          <span>
                            <IconButton
                              color='warning'
                              disabled={isMutating}
                              onClick={() => handleCancel(active)}
                            >
                              <DoNotDisturbIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {active && active.Status === 'submitted' && (
                        <>
                          <Tooltip title='Daten prüfen & übernehmen'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => setDetailFor(active)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Ablehnen'>
                            <span>
                              <IconButton
                                color='error'
                                disabled={isMutating}
                                onClick={() => handleReject(active)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {active &&
                        (active.Status === 'accepted' ||
                          active.Status === 'rejected' ||
                          active.Status === 'cancelled') && (
                          <>
                            <Tooltip title='Daten ansehen'>
                              <span>
                                <IconButton
                                  onClick={() => setDetailFor(active)}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Erneut an Tablet senden'>
                              <span>
                                <IconButton
                                  color='primary'
                                  onClick={() =>
                                    openSendDialog(p.idLegalClient)
                                  }
                                >
                                  <SendIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {unbound.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Eingänge ohne Zuordnung (Freier Modus / Selbst-Auswahl)
          </Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Tablet</TableCell>
                <TableCell>Erzeugt</TableCell>
                <TableCell>Eingegeben</TableCell>
                <TableCell align='right'>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unbound.map(s => (
                <TableRow key={s.idTabletSubmission} hover>
                  <TableCell>
                    <Chip
                      label={STATUS_LABEL[s.Status]}
                      color={STATUS_COLOR[s.Status]}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>{s.TabletName ?? '–'}</TableCell>
                  <TableCell>{formatDate(s.Created_at)}</TableCell>
                  <TableCell>
                    {s.SubmittedAt ? formatDate(s.SubmittedAt) : '–'}
                  </TableCell>
                  <TableCell align='right'>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      justifyContent='flex-end'
                    >
                      {s.Status === 'submitted' && (
                        <>
                          <Tooltip title='Daten prüfen & übernehmen'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => setDetailFor(s)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Ablehnen'>
                            <span>
                              <IconButton
                                color='error'
                                disabled={isMutating}
                                onClick={() => handleReject(s)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {s.Status === 'pending_input' && (
                        <Tooltip title='Auftrag abbrechen'>
                          <span>
                            <IconButton
                              color='warning'
                              disabled={isMutating}
                              onClick={() => handleCancel(s)}
                            >
                              <DoNotDisturbIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {(s.Status === 'accepted' ||
                        s.Status === 'rejected' ||
                        s.Status === 'cancelled') && (
                        <Tooltip title='Daten ansehen'>
                          <span>
                            <IconButton onClick={() => setDetailFor(s)}>
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {unbound.some(s => s.Status === 'submitted') && (
        <Alert severity='info' sx={{ mt: 2 }}>
          Es liegt eine Eingabe ohne Beteiligten-Zuordnung vor. Bitte prüfen und
          einem bestehenden Beteiligten zuweisen oder als neuen Beteiligten
          anlegen.
        </Alert>
      )}

      <SendToTabletDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        idLegalTransaction={idLegalTransaction}
        participants={participants}
        prefillIdLegalClient={dialogPrefillClient}
      />

      <TabletSubmissionDetailDialog
        open={!!detailFor}
        onClose={() => setDetailFor(null)}
        submission={detailFor}
        participants={participants}
      />

      <StartReadingDialog
        open={readingDialogOpen}
        onClose={() => setReadingDialogOpen(false)}
        idLegalTransaction={idLegalTransaction}
        documents={readingDocs}
      />
    </Box>
  );
};
